import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useToast } from "../../../../context/ToastContext";
import apiService from "../../../../services/apiService";
import { Button } from "../../../../components/ui";
import { FaSave, FaTimes } from "react-icons/fa";

const breadcrumb = (
  <span>
    <span
      className="cursor-pointer hover:text-blue-600"
      onClick={() => window.history.back()}
    >
      Home
    </span>{" "}
    &nbsp;/&nbsp;
    <span
      className="cursor-pointer hover:text-blue-600"
      onClick={() => window.history.back()}
    >
      Education Standards
    </span>{" "}
    &nbsp;/&nbsp;
    <span className="text-gray-700">Add New</span>
  </span>
);

const AddEduStandard = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: "",
      code: "",
      uri: "",
      thumb: "",
      status: true,
      order: 0,
      metaTags: {
        keywords: "",
        ogDescription: "",
        ogImage: "",
        ogTitle: "",
      },
    },
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Convert status to boolean if it's a string
      if (typeof data.status === "string") {
        data.status = data.status === "true";
      }

      // Convert order to number
      data.order = Number(data.order) || 0;

      await apiService.request("/i/ims/edustandard", {
        method: "POST",
        body: JSON.stringify(data),
      });

      showToast("success", "Education Standard created successfully");
      navigate("/course-programs/edu-standards");
    } catch (error) {
      console.error("Error creating education standard:", error);
      showToast("error", "Failed to create education standard");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="border-b border-gray-200">
        <div className="p-6">
          <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
            {breadcrumb}
          </nav>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              Add Education Standard
            </h1>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab("basic")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "basic"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Basic Details
              </button>
              <button
                onClick={() => setActiveTab("meta")}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "meta"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Meta Tags
              </button>
            </nav>
          </div>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            {activeTab === "basic" && (
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register("name", { required: "Name is required" })}
                    placeholder="Enter standard name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="code">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="code"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register("code", { required: "Code is required" })}
                    placeholder="e.g., RBSC"
                  />
                  {errors.code && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.code.message}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="uri">
                    URI <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="uri"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register("uri", { required: "URI is required" })}
                    placeholder="e.g., rbsc"
                  />
                  {errors.uri && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.uri.message}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="thumb">
                    Thumbnail
                  </label>
                  <input
                    id="thumb"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Image URL"
                    {...register("thumb")}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="order">
                    Order
                  </label>
                  <input
                    id="order"
                    type="number"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Display order"
                    {...register("order")}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="status">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center">
                    <div className="flex items-center mr-4">
                      <input
                        id="statusActive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={
                          watch("status") === true || watch("status") === "true"
                        }
                        onChange={() => {
                          setValue("status", true);
                        }}
                      />
                      <label htmlFor="statusActive" className="ml-1">
                        Active
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="statusInactive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={
                          watch("status") === false ||
                          watch("status") === "false"
                        }
                        onChange={() => {
                          setValue("status", false);
                        }}
                      />
                      <label htmlFor="statusInactive" className="ml-1">
                        Inactive
                      </label>
                    </div>
                  </div>
                  {errors.status && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.status.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "meta" && (
              <div className="mb-8">
                <div className="mb-4">
                  <label
                    className="block text-gray-700 mb-2"
                    htmlFor="metaTags.ogTitle"
                  >
                    OG Title
                  </label>
                  <input
                    id="metaTags.ogTitle"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register("metaTags.ogTitle")}
                    placeholder="e.g., RBSC Education Standard"
                  />
                </div>

                <div className="mb-4">
                  <label
                    className="block text-gray-700 mb-2"
                    htmlFor="metaTags.keywords"
                  >
                    Keywords
                  </label>
                  <input
                    id="metaTags.keywords"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register("metaTags.keywords")}
                    placeholder="e.g., education, standard, rbsc"
                  />
                </div>

                <div className="mb-4">
                  <label
                    className="block text-gray-700 mb-2"
                    htmlFor="metaTags.ogDescription"
                  >
                    OG Description
                  </label>
                  <textarea
                    id="metaTags.ogDescription"
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                    {...register("metaTags.ogDescription")}
                    placeholder="Enter meta description"
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label
                    className="block text-gray-700 mb-2"
                    htmlFor="metaTags.ogImage"
                  >
                    OG Image
                  </label>
                  <input
                    id="metaTags.ogImage"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Image URL"
                    {...register("metaTags.ogImage")}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <Button
                type="button"
                onClick={() => navigate("/course-programs/edu-standards")}
                variant="outline"
                size="md"
                className="border-gray-300 text-gray-700"
                icon={FaTimes}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={isSubmitting || loading}
                icon={FaSave}
                className={`${isSubmitting || loading ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {isSubmitting || loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddEduStandard;
